/**
 * BELVOIR & CO — Prescription System v2
 * Handles prescription drawer, form building, and cart adds.
 * Depends on: jQuery, window.PRESC_DATA (injected by bcpo-drawer.liquid),
 *             RE_RENDER_DRAWER / OPEN_DRAWER / close_custom_bcpo_drawer (from custom-js.liquid)
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     TRANSITION SWATCH COLOUR MAP
     Keys are lowercase substrings matched against variant title.
     Values are CSS gradient strings for the circle background.
  ───────────────────────────────────────────── */
  var SWATCH_GRADIENTS = {
    brown:  'radial-gradient(circle at 40% 35%, #C4904A, #5C3010)',
    gold:   'radial-gradient(circle at 40% 35%, #C4904A, #5C3010)',
    silver: 'radial-gradient(circle at 40% 35%, #D0D0D0, #606060)',
    grey:   'radial-gradient(circle at 40% 35%, #D0D0D0, #606060)',
    gray:   'radial-gradient(circle at 40% 35%, #D0D0D0, #606060)',
    blue:   'radial-gradient(circle at 40% 35%, #7AAEE8, #1A3B6B)',
    green:  'radial-gradient(circle at 40% 35%, #6BAF6B, #1A4A1A)',
    teal:   'radial-gradient(circle at 40% 35%, #4BAFAF, #0A3A3A)',
    pink:   'radial-gradient(circle at 40% 35%, #E88BB8, #6B1A3A)',
    rose:   'radial-gradient(circle at 40% 35%, #E88BB8, #6B1A3A)',
    purple: 'radial-gradient(circle at 40% 35%, #B88BE8, #3A1A6B)',
    violet: 'radial-gradient(circle at 40% 35%, #B88BE8, #3A1A6B)',
  };

  /* Track selected tier and transition per product */
  var state = {
    selectedTierIndex: 0,
    selectedTransitionVariantId: null,
    uploadedFileName: null,
    uploadedFileUrl: null,   /* set when the file is safely stored */
    uploadPromise: null,     /* pending upload — add-to-cart waits on this */
  };

  /* Prescription file upload backend (Cloudflare Worker + private R2) */
  var RX_UPLOAD_ENDPOINT = 'https://belvoir-rx-upload.belvoir-co.workers.dev/prescription-upload';
  var RX_UPLOAD_TOKEN = '9d127e7a7b680bb85f6baf20e0afa1ea3490dd2159eb4321';
  var RX_MAX_BYTES = 15 * 1024 * 1024;

  /* ─────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────── */

  /** Parse comma-separated or JSON-array value from metafield */
  function parseValues(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(String);
    raw = String(raw).trim();
    /* Try JSON array first */
    if (raw.charAt(0) === '[') {
      try { return JSON.parse(raw).map(String); } catch (e) { /* fall through */ }
    }
    return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  /** Format pennies to £X.XX */
  function formatMoney(pennies) {
    return '£' + (pennies / 100).toFixed(2);
  }

  /** Get a gradient string for a transition variant */
  function swatchGradient(variant) {
    /* If a hex metafield was set, use a simple gradient from it */
    if (variant.hex) {
      return 'radial-gradient(circle at 40% 35%, ' + variant.hex + 'CC, ' + variant.hex + '55)';
    }
    var title = (variant.title || '').toLowerCase();
    for (var key in SWATCH_GRADIENTS) {
      if (title.indexOf(key) !== -1) return SWATCH_GRADIENTS[key];
    }
    return 'radial-gradient(circle at 40% 35%, #999, #333)';
  }

  /** Get current frame variant ID from the product form */
  function getFrameVariantId() {
    var input = document.querySelector('form[action="/cart/add"] input[name="id"]');
    return input ? input.value : null;
  }

  /* ─────────────────────────────────────────────
     PRICE DISPLAY
  ───────────────────────────────────────────── */

  function updateTotalPrice(data) {
    var tier = data.tiers[state.selectedTierIndex];
    if (!tier) return;

    var total = data.productPrice + tier.price;
    if (state.selectedTransitionVariantId) {
      var transVariant = (data.transition.variants || []).find(function (v) {
        return String(v.id) === String(state.selectedTransitionVariantId);
      });
      if (transVariant) total += transVariant.price;
    }

    var el = document.getElementById('presc-total-price');
    if (el) el.innerHTML = '<span>' + formatMoney(total) + '</span>';
  }

  /* ─────────────────────────────────────────────
     FORM BUILDER — SPH / CYL / AXIS grid + PD
  ───────────────────────────────────────────── */

  function buildSelect(name, values, placeholder) {
    var opts = '<option value="">' + (placeholder || '—') + '</option>';
    values.forEach(function (v) {
      opts += '<option value="' + v + '">' + v + '</option>';
    });
    return '<select class="presc-select" name="' + name + '">' + opts + '</select>';
  }

  function buildGrid(tier) {
    var sph  = parseValues(tier.sphValues);
    var cyl  = parseValues(tier.cylValues);
    var axis = parseValues(tier.axisValues);
    var add  = tier.addValues ? parseValues(tier.addValues) : null;

    var rows = [
      { label: 'Sphere (SPH)', leftName: 'sph_left',  rightName: 'sph_right',  values: sph  },
      { label: 'Cylinder (CYL)', leftName: 'cyl_left', rightName: 'cyl_right', values: cyl  },
      { label: 'AXIS',           leftName: 'axis_left', rightName: 'axis_right', values: axis },
    ];

    /* Progressive lenses require an ADD (near addition) power — shown as TOP column */
    if (add && add.length > 0) {
      rows.push({ label: 'ADD', leftName: 'add_left', rightName: 'add_right', values: add });
    }

    var html = '<div class="presc-grid">';
    html += '<div class="presc-grid-header">';
    html += '<div class="presc-grid-header-spacer" style="display:block!important">OPTION</div>';
    html += '<div class="presc-grid-col-label">LEFT (OS)</div>';
    html += '<div class="presc-grid-col-label">RIGHT (OD)</div>';
    html += '</div>';

    rows.forEach(function (row) {
      html += '<div class="presc-grid-row">';
      html += '<div class="presc-grid-label">' + row.label + '</div>';
      html += '<div class="presc-grid-cell">' + buildSelect(row.leftName, row.values) + '</div>';
      html += '<div class="presc-grid-cell">' + buildSelect(row.rightName, row.values) + '</div>';
      html += '</div>';
    });

    html += '</div>';
    return html;
  }

  function buildPDRow(tier) {
    var opts = '<option value="">—</option>';
    for (var i = tier.pdMin; i <= tier.pdMax; i++) {
      opts += '<option value="' + i + '">' + i + ' mm</option>';
    }

    return '<div class="presc-pd-row">' +
      '<div class="presc-pd-label">Pupillary Distance (PD)</div>' +
      '<div class="presc-pd-field">' +
        '<select class="presc-select" name="pd">' + opts + '</select>' +
        '<a href="#" class="bcpo_link" pd_read style="margin-top:0.6rem;display:inline-block;">WHAT IS PD?</a>' +
      '</div>' +
    '</div>';
  }

  function renderForm(data, tierIndex) {
    var tier = data.tiers[tierIndex];
    if (!tier) return;

    var fieldsEl = document.getElementById('presc-fields-container');
    var pdEl = document.getElementById('presc-pd-container');
    if (fieldsEl) {
      fieldsEl.innerHTML = buildGrid(tier);
      /* Force browser repaint so :empty pseudo-class re-evaluates on new content */
      var spacer = fieldsEl.querySelector('.presc-grid-header-spacer');
      if (spacer) {
        spacer.style.display = 'none';
        spacer.offsetHeight; /* trigger reflow */
        spacer.style.display = '';
      }
    }
    if (pdEl) pdEl.innerHTML = buildPDRow(tier);

    /* Update form screen heading */
    var heading = document.getElementById('presc-form-heading');
    if (heading) heading.textContent = (tier.label || 'PRESCRIPTION').toUpperCase();

    updateTotalPrice(data);
  }

  /* ─────────────────────────────────────────────
     TRANSITION SWATCHES
  ───────────────────────────────────────────── */

  function buildTransitionSwatches(data) {
    var el = document.getElementById('presc-transition-swatches');
    if (!el) return;

    var variants = data.transition.variants || [];
    if (!variants.length) return;

    /* Set price label — use innerHTML so money span renders correctly */
    var priceLabel = document.getElementById('presc-transition-price-label');
    if (priceLabel && variants[0]) priceLabel.innerHTML = '+' + variants[0].priceFmt;

    var html = '';

    /* Variant image tiles */
    variants.forEach(function (v) {
      var imgSrc = v.image ? v.image + '&width=120' : '';
      var imgHtml = imgSrc
        ? '<img src="' + imgSrc + '" alt="' + v.title + '" style="width:100%;height:100%;object-fit:cover;border-radius:0.4rem;display:block;">'
        : '<div style="width:100%;height:100%;border-radius:0.4rem;background:rgba(var(--color-foreground),0.1);"></div>';

      html += '<div class="presc-swatch-tile" data-variant-id="' + v.id + '">' +
                '<div class="presc-swatch-img">' + imgHtml + '</div>' +
                '<div class="presc-swatch-name">' + v.title + '</div>' +
              '</div>';
    });

    /* None tile */
    html += '<div class="presc-swatch-tile presc-swatch-tile--none selected" data-variant-id="">' +
              '<div class="presc-swatch-img presc-swatch-img--none">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="4" y1="4" x2="20" y2="20"/><line x1="20" y1="4" x2="4" y2="20"/></svg>' +
              '</div>' +
              '<div class="presc-swatch-name">None</div>' +
            '</div>';

    el.innerHTML = html;

    /* Events */
    $(el).on('click', '.presc-swatch-tile', function () {
      var variantId = $(this).data('variant-id');
      $(el).find('.presc-swatch-tile').removeClass('selected');
      $(this).addClass('selected');

      state.selectedTransitionVariantId = variantId || null;

      /* Update label */
      var label = document.getElementById('presc-transition-label');
      if (label) {
        if (!variantId) {
          label.textContent = 'None';
        } else {
          var match = variants.find(function (v) { return String(v.id) === String(variantId); });
          label.textContent = match ? match.title : '';
        }
      }

      updateTotalPrice(data);
    });
  }

  /* ─────────────────────────────────────────────
     TIER SELECTION
  ───────────────────────────────────────────── */

  function setupTierSelection(data) {
    $(document).on('click', '#presc-tier-list .bcpo_option', function () {
      var $this = $(this);

      /* Skip cards without data-tier-index (e.g. free quote card) */
      if (!($this.is('[data-tier-index]'))) return;

      var index = parseInt($this.data('tier-index'), 10);
      state.selectedTierIndex = index;

      /* Highlight selected */
      $('#presc-tier-list .bcpo_option').removeClass('selected');
      $this.addClass('selected');

      /* Render form with this tier's data */
      renderForm(data, index);

      /* Open form screen */
      openFormScreen();
    });
  }

  /* ─────────────────────────────────────────────
     SCREEN MANAGEMENT
  ───────────────────────────────────────────── */

  function openFormScreen() {
    $('#presc-form-screen').addClass('active');
    var fill = document.getElementById('presc-progress-fill');
    if (fill) fill.style.width = '100%';
  }

  function closeFormScreen() {
    $('#presc-form-screen').removeClass('active');
    var fill = document.getElementById('presc-progress-fill');
    if (fill) fill.style.width = '50%';
  }

  /* ─────────────────────────────────────────────
     FILE UPLOAD
  ───────────────────────────────────────────── */

  function setupFileUpload() {
    $(document).on('change', '#presc-file-input', function () {
      var file = this.files[0];
      var el = document.getElementById('presc-upload-name');

      state.uploadedFileName = file ? file.name : null;
      state.uploadedFileUrl = null;
      state.uploadPromise = null;

      if (!file) {
        if (el) el.textContent = 'No file chosen';
        return;
      }

      /* Too large — keep filename, fall back to email flow */
      if (file.size > RX_MAX_BYTES) {
        if (el) el.textContent = file.name + ' (too large to upload — please email it)';
        return;
      }

      if (el) el.textContent = 'Uploading ' + file.name + '…';

      var formData = new FormData();
      formData.append('file', file);

      /* Never rejects — on any failure we fall back to filename + email,
         so checkout is never blocked by the upload backend. */
      state.uploadPromise = fetch(RX_UPLOAD_ENDPOINT, {
        method: 'POST',
        headers: { 'X-Upload-Token': RX_UPLOAD_TOKEN },
        body: formData,
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (json) {
          if (json && json.url) {
            state.uploadedFileUrl = json.url;
            if (el) el.textContent = '✓ ' + file.name;
          } else {
            if (el) el.textContent = file.name + ' (upload failed — please email it)';
          }
        })
        .catch(function () {
          if (el) el.textContent = file.name + ' (upload failed — please email it)';
        });
    });
  }

  /* ─────────────────────────────────────────────
     CART ADD
  ───────────────────────────────────────────── */

  /**
   * Collect all form field values from the prescription form.
   * Returns null if any required field is empty.
   */
  function collectFormValues() {
    var values = {};
    var valid = true;

    ['sph_left', 'sph_right', 'cyl_left', 'cyl_right', 'axis_left', 'axis_right', 'pd'].forEach(function (name) {
      var el = document.querySelector('#presc-form-screen select[name="' + name + '"]');
      if (!el) return;
      var val = el.value;
      if (!val) valid = false;
      values[name] = val;
    });

    /* TOP (ADD) fields — only present for progressive tiers */
    ['add_left', 'add_right'].forEach(function (name) {
      var el = document.querySelector('#presc-form-screen select[name="' + name + '"]');
      if (!el) return; /* not shown for this tier — skip */
      var val = el.value;
      if (!val) valid = false;
      values[name] = val;
    });

    return valid ? values : null;
  }

  /**
   * Format collected values into line item properties matching the order screenshot format:
   * Sphere (SPH): (Left (OS): -0.75, Right (OD): -1.00)
   */
  function buildLineItemProperties(productTitle, tier, formValues, groupId) {
    var props = {};

    /* Hidden group link — pairs this fee with its exact frame line (survives
       duplicate frames with identical titles). Underscore prefix hides it in
       cart + checkout. */
    if (groupId) props['_group_id'] = groupId;

    /* Visible frame reference */
    props['Frame'] = productTitle;

    /* Prescription values in the format shown in order screenshots */
    props['Sphere (SPH)']   = '(Left (OS): ' + formValues.sph_left  + ', Right (OD): ' + formValues.sph_right  + ')';
    props['Cylinder (CYL)'] = '(Left (OS): ' + formValues.cyl_left  + ', Right (OD): ' + formValues.cyl_right  + ')';
    props['AXIS']            = '(Left (OS): ' + formValues.axis_left + ', Right (OD): ' + formValues.axis_right + ')';
    props['PD']              = formValues.pd;

    /* TOP (ADD) — progressive lenses only */
    if (formValues.add_left && formValues.add_right) {
      props['ADD'] = '(Left (OS): ' + formValues.add_left + ', Right (OD): ' + formValues.add_right + ')';
    }

    /* File upload — stored URL when upload succeeded, filename fallback otherwise */
    if (state.uploadedFileUrl) {
      props['_Prescription File'] = state.uploadedFileUrl;
    } else if (state.uploadedFileName) {
      props['_Prescription File'] = state.uploadedFileName + ' (please also email to contact@belvoirco.com)';
    }

    return props;
  }

  function setupAddToCart(data) {
    $(document).on('click', '.presc-v2-add-btn', function () {
      var $btn = $(this);

      /* Prevent double-click */
      if ($btn.hasClass('loading') || $btn.data('processing')) return;

      /* 1. Validate checkbox */
      var $checkbox = $('#presc-confirm-checkbox');
      if (!$checkbox.hasClass('checked')) {
        $checkbox.addClass('unchecked');
        var $body = $('#presc-form-screen .option_drawer_body');
        $body.animate({
          scrollTop: $checkbox.offset().top - $body.offset().top + $body.scrollTop()
        }, 400);
        return;
      }

      /* 2. Validate all prescription fields */
      var formValues = collectFormValues();
      if (!formValues) {
        /* Highlight empty selects */
        $('#presc-form-screen select').each(function () {
          if (!this.value) $(this).addClass('presc-select--error');
        });
        return;
      }
      $('#presc-form-screen select').removeClass('presc-select--error');

      /* 3. Get frame variant */
      var frameVariantId = getFrameVariantId();
      if (!frameVariantId) {
        console.error('Prescription: could not find frame variant ID');
        return;
      }

      var tier = data.tiers[state.selectedTierIndex];
      if (!tier) return;

      /* 4. Lock button */
      var originalText = $btn.data('original-text') || 'ADD TO BAG';
      $btn.addClass('loading').data('processing', true).text('Adding...');
      window._skipCartInterceptor = true;

      /* 4b. If a prescription file is still uploading, wait for it first
         (resolves on failure too — fallback text is used in that case). */
      var uploadWait = state.uploadPromise || Promise.resolve();

      uploadWait.then(function () {

      /* 5. Build properties — one group ID links frame + fee + transition */
      var groupId = 'rx-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
      var prescProps = buildLineItemProperties(data.productTitle, tier, formValues, groupId);

      /* 6. Sequential cart adds */
      addFrameToCart(frameVariantId, groupId)
        .then(function () {
          return addPrescriptionFeeToCart(tier, prescProps);
        })
        .then(function () {
          if (state.selectedTransitionVariantId) {
            return addTransitionToCart(state.selectedTransitionVariantId, data.productTitle, groupId);
          }
        })
        .then(function () {
          /* Success */
          if (typeof close_custom_bcpo_drawer === 'function') close_custom_bcpo_drawer();
          if (typeof RE_RENDER_DRAWER === 'function') RE_RENDER_DRAWER();

          setTimeout(function () {
            if (typeof OPEN_DRAWER === 'function') OPEN_DRAWER();
            $('cart-drawer').removeClass('is-empty');

            /* Reset form */
            resetForm(data);

            $btn.removeClass('loading').data('processing', false).text(originalText);
            window._skipCartInterceptor = false;

            /* Our cart adds bypass the mystery-gift interceptor via
               _skipCartInterceptor, so re-evaluate eligibility manually. */
            if (window.MysteryGift && typeof window.MysteryGift.check === 'function') {
              window.MysteryGift.check();
            }
          }, 1000);
        })
        .catch(function (err) {
          console.error('Prescription cart add error:', err);
          window._skipCartInterceptor = false;
          /* Inline error on the button — no blocking alert() */
          $btn.removeClass('loading').data('processing', false)
              .addClass('presc-v2-add-btn--error')
              .text('SOMETHING WENT WRONG — TRY AGAIN');
          setTimeout(function () {
            $btn.removeClass('presc-v2-add-btn--error').text(originalText);
          }, 4000);
        });

      }); /* end uploadWait */
    });
  }

  function addFrameToCart(variantId, groupId) {
    var payload = { id: variantId, quantity: 1 };
    /* The group ID keeps this frame line distinct from other units of the same
       frame, so each prescription bundle stays its own line + fee pairing. */
    if (groupId) payload['properties[_group_id]'] = groupId;
    return $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      dataType: 'json',
      data: payload
    });
  }

  function addPrescriptionFeeToCart(tier, properties) {
    return $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      dataType: 'json',
      data: {
        id: tier.variantId,
        quantity: 1,
        properties: properties
      }
    });
  }

  function addTransitionToCart(variantId, frameTitle, groupId) {
    var props = { 'Frame': frameTitle };
    if (groupId) props['_group_id'] = groupId;
    return $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      dataType: 'json',
      data: {
        id: variantId,
        quantity: 1,
        properties: props
      }
    });
  }

  /* ─────────────────────────────────────────────
     RESET
  ───────────────────────────────────────────── */

  function resetForm(data) {
    state.selectedTierIndex = 0;
    state.selectedTransitionVariantId = null;
    state.uploadedFileName = null;
    state.uploadedFileUrl = null;
    state.uploadPromise = null;

    /* Reset selects */
    $('#presc-form-screen select').val('');

    /* Reset checkbox */
    $('#presc-confirm-checkbox').removeClass('checked unchecked');

    /* Reset file name */
    var nameEl = document.getElementById('presc-upload-name');
    if (nameEl) nameEl.textContent = 'No file chosen';
    var fileInput = document.getElementById('presc-file-input');
    if (fileInput) fileInput.value = '';

    /* Reset transition */
    $('.presc-swatch-tile').removeClass('selected');
    $('.presc-swatch-tile--none').addClass('selected');
    var label = document.getElementById('presc-transition-label');
    if (label) label.textContent = 'None';

    /* Reset tier highlight */
    $('#presc-tier-list .bcpo_option').removeClass('selected');
    $('#presc-tier-list .bcpo_option').first().addClass('selected');

    /* Close form screen, return to tier selection */
    closeFormScreen();

    /* Re-render form with default tier */
    renderForm(data, 0);

    updateTotalPrice(data);
  }

  /* ─────────────────────────────────────────────
     DRAWER NAVIGATION EVENTS
  ───────────────────────────────────────────── */

  function setupDrawerNavigation() {
    /* Back button (icon-caret in option_drawer_head) */
    $(document).on('click', '#presc-form-screen .option_drawer_head .icon-caret', function () {
      closeFormScreen();
    });

    /* Close button (icon-close in option_drawer_head) */
    $(document).on('click', '#presc-form-screen .option_drawer_head .icon-close', function () {
      if (typeof close_custom_bcpo_drawer === 'function') close_custom_bcpo_drawer();
    });

    /* Clear error highlight on select change */
    $(document).on('change', '#presc-form-screen .presc-select', function () {
      $(this).removeClass('presc-select--error');
    });
  }

  /* ─────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────── */

  function init() {
    /* Find which product is on this page */
    var drawerEl = document.querySelector('.prescription-drawer__inner[data-product-id]');
    if (!drawerEl) return;

    var productId = parseInt(drawerEl.getAttribute('data-product-id'), 10);
    var data = window.PRESC_DATA && window.PRESC_DATA[productId];
    if (!data || !data.tiers || !data.tiers.length) return;

    console.log('✅ Prescription system v2 initialised for product', productId);

    /* Set initial progress bar to 50% (tier selection screen) */
    var fill = document.getElementById('presc-progress-fill');
    if (fill) fill.style.width = '50%';

    /* Build initial form with first tier */
    renderForm(data, 0);

    /* Build transition swatches */
    if (data.hasTransition && data.transition && data.transition.variants.length) {
      buildTransitionSwatches(data);
    }

    /* Wire up events */
    setupTierSelection(data);
    setupAddToCart(data);
    setupFileUpload();
    setupDrawerNavigation();
  }

  /* Run after DOM ready */
  $(document).ready(function () {
    init();
  });

})();